import type {
  DragStart,
  DragUpdate,
  DropResult,
  OnDragEndResponder,
  OnDragStartResponder,
  OnDragUpdateResponder,
  ResponderProvided,
} from "@hello-pangea/dnd";
import { useCallback, useEffect, useRef } from "react";
import { useGlobalDragNDropContext } from "./context";
import type { IGlobalDragNDropEvent } from "./types";

export const useGlobalDragNDropEvent = (event: IGlobalDragNDropEvent) => {
  const { registerEvent, unregisterEvent } = useGlobalDragNDropContext();

  useEffect(() => {
    registerEvent(event);
    return () => unregisterEvent(event);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);
};

export const useGlobalDragNDrop = () => {
  const events = useRef<IGlobalDragNDropEvent[]>([]);
  const eventsDragStart = useRef<OnDragStartResponder[]>([]);
  const eventsDragUpdate = useRef<OnDragUpdateResponder[]>([]);
  const eventsDragEnd = useRef<OnDragEndResponder[]>([]);

  const registerEvent = useCallback((event: IGlobalDragNDropEvent) => {
    events.current.push(event);
    return event;
  }, []);
  const unregisterEvent = useCallback((event: IGlobalDragNDropEvent) => {
    const index = events.current.findIndex((v) => v === event);
    if (index !== -1) {
      events.current.splice(index, 1);
    }
  }, []);

  const registerDragStart = useCallback((callback: OnDragStartResponder) => {
    eventsDragStart.current.push(callback);
    return callback;
  }, []);
  const unregisterDragStart = useCallback((callback: OnDragStartResponder) => {
    const index = eventsDragStart.current.findIndex((v) => v === callback);
    if (index !== -1) {
      eventsDragStart.current.splice(index, 1);
    }
  }, []);

  const registerDragUpdate = useCallback((callback: OnDragUpdateResponder) => {
    eventsDragUpdate.current.push(callback);
    return callback;
  }, []);
  const unregisterDragUpdate = useCallback((callback: OnDragUpdateResponder) => {
    const index = eventsDragUpdate.current.findIndex((v) => v === callback);
    if (index !== -1) {
      eventsDragUpdate.current.splice(index, 1);
    }
  }, []);

  const registerDragEnd = useCallback((callback: OnDragEndResponder) => {
    eventsDragEnd.current.push(callback);
    return callback;
  }, []);
  const unregisterDragEnd = useCallback((callback: OnDragEndResponder) => {
    const index = eventsDragEnd.current.findIndex((v) => v === callback);
    if (index !== -1) {
      eventsDragEnd.current.splice(index, 1);
    }
  }, []);

  const onDragStart = useCallback((start: DragStart, provided: ResponderProvided) => {
    for (const event of eventsDragStart.current) {
      event(start, provided);
    }
  }, []);

  const onDragUpdate = useCallback((update: DragUpdate, provided: ResponderProvided) => {
    for (const event of eventsDragUpdate.current) {
      event(update, provided);
    }
  }, []);

  const onDragEnd = useCallback((result: DropResult, provided: ResponderProvided) => {
    for (const event of events.current) {
      if (event.validate(result, provided)) {
        event.execute(result, provided);
        break;
      }
    }
    for (const event of eventsDragEnd.current) {
      event(result, provided);
    }
  }, []);

  return {
    onDragStart,
    onDragUpdate,
    onDragEnd,
    registerEvent,
    registerDragStart,
    registerDragUpdate,
    registerDragEnd,
    unregisterEvent,
    unregisterDragStart,
    unregisterDragUpdate,
    unregisterDragEnd,
  };
};
